import { CUE_LIMITS, clampCue, type CueParams } from '@sol-pool/engine-physics'

/**
 * A escala VISÍVEL de um taco: cada atributo de 0 a 100, inteiro.
 *
 * 0 é o pior limite que a física permite, 100 o melhor, e entre eles é
 * linear. É nesta escala que o orçamento por raridade é somado e que as
 * barras do lobby são desenhadas. A física continua em bps — esta é a única
 * tradução, e nos dois sentidos, para o servidor mandar pontos e o cliente não
 * precisar recalcular nada.
 *
 * A MIRA NÃO ESTÁ AQUI, e é de propósito.
 *
 * Até o ruleset v2 ela era o terceiro atributo: `aimBps` era o passo da grade
 * de direções do taco, e o NFT comprava finura de mira. A física v6 tirou a
 * grade — todo taco aponta na resolução do formato de replay — então não há
 * mais nada para pontuar. Quem ainda quiser ler os números daquela época
 * encontra V1 e V2 em `rulesets-congelados.ts`.
 */
export type Pontos = { peso: number; efeito: number; aderencia: number; quebra: number }

export const ATRIBUTOS = ['peso', 'efeito', 'aderencia', 'quebra'] as const satisfies readonly (keyof Pontos)[]

type Eixo = { pior: number; melhor: number }
const EIXOS: Record<keyof Pontos, Eixo> = {
  peso: { pior: CUE_LIMITS.minMassBps, melhor: CUE_LIMITS.maxMassBps },
  efeito: { pior: CUE_LIMITS.minSpinBps, melhor: CUE_LIMITS.maxSpinBps },
  aderencia: { pior: CUE_LIMITS.minClothGripBps, melhor: CUE_LIMITS.maxClothGripBps },
  quebra: { pior: CUE_LIMITS.minBreakBonusBps, melhor: CUE_LIMITS.maxBreakBonusBps },
}

const prender = (v: number) => Math.max(0, Math.min(100, v))

const paraBps = (eixo: Eixo, pontos: number): number =>
  Math.round(eixo.pior + ((eixo.melhor - eixo.pior) * prender(pontos)) / 100)

const paraPontos = (eixo: Eixo, bps: number): number =>
  prender(Math.round(((bps - eixo.pior) * 100) / (eixo.melhor - eixo.pior)))

/** Pontos → bps, já presos aos limites da física. */
export function paramsDePontos(p: Pontos): CueParams {
  return clampCue({
    massBps: paraBps(EIXOS.peso, p.peso),
    spinBps: paraBps(EIXOS.efeito, p.efeito),
    // A mira é a mesma para todo taco: `clampCue` a fixa no padrão.
    aimBps: 10_000,
    clothGripBps: paraBps(EIXOS.aderencia, p.aderencia),
    breakBonusBps: paraBps(EIXOS.quebra, p.quebra),
  })
}

/** bps → pontos, para as barras e para a tabela de equilíbrio. */
export function pontosDeParams(c: CueParams): Pontos {
  return {
    peso: paraPontos(EIXOS.peso, c.massBps),
    efeito: paraPontos(EIXOS.efeito, c.spinBps),
    aderencia: paraPontos(EIXOS.aderencia, c.clothGripBps),
    quebra: paraPontos(EIXOS.quebra, c.breakBonusBps),
  }
}

export const somaDePontos = (p: Pontos): number => ATRIBUTOS.reduce((s, a) => s + p[a], 0)
